"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowLeft, Clock, Cpu, Layers, Sparkles, Users, CircleCheck as CheckCircle2, Calendar, Languages, ChevronRight, Info as HelpCircle, CirclePlay as PlayCircle, Wrench, Code as Code2, Presentation, Award, Milestone, Compass, Laptop, Coins } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function RoboticsCourseHP4Detail() {
  const [lang, setLang] = React.useState<"vi" | "en">("vi");
  const t = {
    vi: {
      breadcrumbs: {
        home: "Trang chủ",
        pathway: "Lộ trình Robotics",
        ageTrack: "Robotics 7+ tuổi",
        current: "HP4 – Xe robot thông minh"
      },
      backPathway: "QUAY LẠI LỘ TRÌNH ROBOTICS",
      courseCode: "HP4",
      title: "Xe robot thông minh",
      subtitle: "Học phần 4: Khám phá cảm biến tương tác và thuật toán phản xạ",
      quote: "Robot biết phản ứng trước môi trường.",
      heroConsultCta: "ĐĂNG KÝ TƯ VẤN",
      heroPathwayCta: "XEM TOÀN BỘ LỘ TRÌNH",
      metaAge: "Từ 7 tuổi",
      metaPlatform: "WhalesBot AI Module 1S",
      metaDuration: "12 buổi × 90 phút",
      metaGroup: "2–3 học sinh/bộ kit",
      metaPrereq: "Hoàn thành HP3",
      labels: {
        age: "Độ tuổi phù hợp",
        platform: "Bộ công cụ / Giáo cụ",
        duration: "Thời lượng đào tạo",
        groupSize: "Quy mô nhóm học",
        prerequisite: "Điều kiện tiên quyết"
      },
      targetSection: {
        tag: "Đối tượng học viên",
        title: "Khóa học này dành cho ai?",
        desc: "Dành cho học viên đã hoàn thành HP3. HP4 tập trung vào cảm biến tương tác và robot phản xạ thông minh với môi trường.",
        highlights: ["Học sinh đã hoàn thành HP3 (Xe robot đầu tiên), nắm vững cấu trúc truyền động cơ bản.", "Muốn tìm hiểu cơ chế nhận biết môi trường qua cảm biến chạm và cảm biến hồng ngoại.", "Phát triển thuật toán phản xạ: phát hiện va chạm, tránh chướng ngại vật, chống rơi mép bàn và dừng theo vạch.", "Hình thành tư duy logic lập trình có điều kiện (If-Else, vòng lặp, biến đếm, bộ đếm thời gian)."]
      },
      timeline: {
        tag: "Cấu trúc 12 buổi học",
        title: "Lộ trình đào tạo 6 giai đoạn",
        desc: "12 buổi học được chia thành 6 giai đoạn logic, tập trung hoàn toàn vào các nội dung cảm biến, cơ cấu và thuật toán chính thức của HP4.",
        stages: [{
          stage: "Giai đoạn 1",
          sessions: "Buổi 1 – 2",
          title: "Cảm biến chạm & Cảm biến hồng ngoại",
          topics: "Touch sensor, Infrared sensor",
          desc: "Tìm hiểu nguyên lý hoạt động của cảm biến chạm (Touch sensor) và cảm biến hồng ngoại (Infrared sensor) để nhận biết bề mặt và tiếp xúc cơ học."
        }, {
          stage: "Giai đoạn 2",
          sessions: "Buổi 3 – 4",
          title: "Phát hiện va chạm & Đẩy vật cản thụ động",
          topics: "Collision detection, Passive pushing",
          desc: "Thiết kế cơ cấu gạt/đẩy thụ động (Passive pushing) và xây dựng phản xạ cơ bản khi nhận diện va chạm (Collision detection)."
        }, {
          stage: "Giai đoạn 3",
          sessions: "Buổi 5 – 6",
          title: "Tránh chướng ngại vật & Cấu trúc If-Else",
          topics: "Obstacle avoidance, If-Else",
          desc: "Ứng dụng cảm biến hồng ngoại để phát hiện vật cản từ xa (Obstacle avoidance) kết hợp rẽ nhánh điều kiện If-Else để chủ động chuyển hướng."
        }, {
          stage: "Giai đoạn 4",
          sessions: "Buổi 7 – 8",
          title: "Dừng theo vạch & Chống rơi vực",
          topics: "Line stopping, Anti-fall",
          desc: "Hiệu chỉnh ngưỡng hồng ngoại mặt sàn để nhận diện vạch đen (Line stopping) và xây dựng thuật toán an toàn chống rơi vực (Anti-fall) tại mép bàn."
        }, {
          stage: "Giai đoạn 5",
          sessions: "Buổi 9 – 10",
          title: "Đếm vạch kẻ, Vòng lặp & Bộ đếm",
          topics: "Counting lines, Loops, Counters",
          desc: "Tích hợp vòng lặp tuần hoàn (Loops) và biến đếm (Counters) nhằm thực hiện nhiệm vụ đếm số vạch (Counting lines) và định vị không gian."
        }, {
          stage: "Giai đoạn 6",
          sessions: "Buổi 11 – 12",
          title: "Giá trị ngẫu nhiên, Timer & Dự án tổng hợp",
          topics: "Random, Timer",
          desc: "Ứng dụng bộ đếm thời gian (Timer) và sinh giá trị ngẫu nhiên (Random) cho hành vi tự hành phản xạ, tổng kết báo cáo sản phẩm."
        }]
      },
      methodology: {
        tag: "Quy trình sư phạm 4 bước",
        title: "Phương pháp học tập chuẩn mực",
        desc: "Mô hình 4 bước xuyên suốt giúp học sinh chuyển hóa quan sát thực tế thành mô hình phần cứng và dòng lệnh điều khiển.",
        steps: [{
          step: "01",
          title: "Khởi động",
          desc: "Quan sát tình huống thực tế, phân tích vấn đề va chạm và xác định phản xạ cần có cho robot."
        }, {
          step: "02",
          title: "Lắp ráp",
          desc: "Lắp ráp phần cứng xe robot, tối ưu vị trí cảm biến chạm, cảm biến hồng ngoại và cơ cấu đẩy thụ động."
        }, {
          step: "03",
          title: "Lập trình",
          desc: "Xây dựng thuật toán logic If-Else, bộ đếm, vòng lặp và xử lý tín hiệu phản hồi từ cảm biến theo thời gian thực."
        }, {
          step: "04",
          title: "Trình bày",
          desc: "Thử nghiệm trên sa bàn thực tế, tinh chỉnh thông số cảm biến và tự tin thuyết trình giải pháp kỹ thuật."
        }]
      },
      equipmentCard: {
        tag: "Trang thiết bị & Giáo cụ",
        title: "Bộ kit WhalesBot AI Module 1S",
        desc: "Bộ giáo cụ robotics tiêu chuẩn quốc tế với đầy đủ vi điều khiển trung tâm, mô-tơ truyền động và các cảm biến phản xạ chuyên dụng.",
        refCostLabel: "Chi phí thiết bị tham khảo",
        refCostValue: "~6.500.000 VNĐ / bộ kit",
        refCostNote: "* Đây là chi phí thiết bị tham khảo (không phải học phí khóa học).",
        specs: [{
          label: "Giáo cụ chính",
          value: "WhalesBot AI Module 1S"
        }, {
          label: "Thiết bị học tập",
          value: "Yêu cầu máy tính (Laptop/PC) để lập trình"
        }, {
          label: "Cảm biến phần cứng",
          value: "Cảm biến chạm (Touch) & Cảm biến hồng ngoại (IR)"
        }, {
          label: "Phần mềm",
          value: "WhalesBot Scratch / Block-based IDE"
        }]
      },
      groupModel: {
        tag: "Mô hình lớp học",
        title: "Quy mô học tập nhóm nhỏ",
        desc: "Tối ưu hóa thời gian tương tác giữa giáo viên và học sinh, rèn luyện kỹ năng phân vai kỹ thuật và làm việc nhóm hiệu quả.",
        idealTitle: "2 học sinh / bộ kit (Lý tưởng)",
        idealDesc: "Mô hình khuyến nghị tối ưu nhất: 1 học sinh phụ trách kỹ thuật lắp ráp & cân chỉnh cơ cấu, 1 học sinh phụ trách thuật toán & nạp chương trình.",
        optTitle: "3 học sinh / bộ kit (Tối ưu chi phí)",
        optDesc: "Phương án luân phiên vai trò (Lắp ráp – Lập trình – Thử nghiệm sa bàn), đảm bảo mọi học sinh đều làm chủ trọn vẹn kỹ năng thực hành."
      },
      finalProject: {
        tag: "Dự án tốt nghiệp mẫu",
        name: "Robot dọn sân trường",
        nameEn: "Schoolyard Cleaning Robot",
        desc: "Dự án tổng hợp mẫu của HP4: Xe robot tích hợp cơ cấu gạt đẩy thụ động, cảm biến chạm nhận diện va chạm và cảm biến hồng ngoại chống rơi vực để tự hành dọn dẹp khuôn viên an toàn.",
        highlights: ["Tự động gạt đẩy và gom rác/vật thể trên mặt sàn", "Phản xạ tức thì khi chạm chướng ngại vật: lùi lại và đổi hướng di chuyển", "Cảm biến hồng ngoại nhận diện mép sàn/vực dốc, ngăn robot rơi ngã", "Đếm số lần va chạm hoặc vạch mốc và dừng lại khi hoàn thành chu trình"]
      },
      roadmapSection: {
        tag: "Bản đồ lộ trình 10 học phần",
        title: "Vị trí của HP4 trong lộ trình Robotics",
        desc: "Lộ trình đào tạo 10 cấp độ từ Mầm non (D1–D4) đến Tiểu học & THCS chuyên sâu (HP1–HP6).",
        currentBadge: "Đang xem"
      },
      nextStepCard: {
        tag: "Học phần kế tiếp",
        title: "Bước tiếp theo: HP5 – Thành phố xe tự lái",
        titleEn: "Autonomous City",
        desc: "Sau khi làm chủ các phản xạ va chạm và an toàn cơ bản ở HP4, học sinh tiến bước vào không gian giao thông thông minh với sa bàn đa tác vụ, nhận diện đèn tín hiệu và trạm tự hành.",
        cta: "XEM HP5 →"
      },
      focusHeader: "Trọng tâm cảm biến & thuật toán HP4",
      sensorsHeader: "Cảm biến & Tương tác vật lý",
      codingHeader: "Tư duy logic & Thuật toán",
      prevBtn: "← HP3 — Xe robot đầu tiên",
      nextBtn: "HP5 — Thành phố xe tự lái →",
      trialCta: "Đăng ký học thử khóa này"
    },
    en: {
      breadcrumbs: {
        home: "Home",
        pathway: "Robotics Pathway",
        ageTrack: "Robotics 7+ Years",
        current: "HP4 – Smart Autonomous Vehicle"
      },
      backPathway: "BACK TO ROBOTICS PATHWAY",
      courseCode: "HP4",
      title: "Smart Autonomous Vehicle",
      subtitle: "Module 4: Environmental Interaction, Sensors & Reactive Logic",
      quote: "Robots that sense and actively respond to their environment.",
      heroConsultCta: "BOOK CONSULTATION",
      heroPathwayCta: "VIEW FULL PATHWAY",
      metaAge: "From 7 years old",
      metaPlatform: "WhalesBot AI Module 1S",
      metaDuration: "12 sessions × 90 mins",
      metaGroup: "2–3 students / kit",
      metaPrereq: "Completed HP3",
      labels: {
        age: "Target Age",
        platform: "Hardware Kit",
        duration: "Course Duration",
        groupSize: "Class Density",
        prerequisite: "Prerequisites"
      },
      targetSection: {
        tag: "Target Learners",
        title: "Who is this course for?",
        desc: "For learners who completed HP3. HP4 focuses on interactive sensors and robots that intelligently react to their environment.",
        highlights: ["Students who completed HP3 (First Robot Car) and master fundamental drivetrain mechanics.", "Learners seeking to master environmental sensing via touch and infrared sensors.", "Developing reactive algorithms: collision detection, obstacle avoidance, line stopping, and anti-fall safety.", "Building structured conditional logic (If-Else, loops, counters, timer triggers)."]
      },
      timeline: {
        tag: "12-Session Curriculum",
        title: "6-Stage Learning Roadmap",
        desc: "12 sessions organized across 6 pedagogical stages, directly mapping HP4's official sensor, mechanism, and logic competencies.",
        stages: [{
          stage: "Stage 1",
          sessions: "Sessions 1 – 2",
          title: "Touch Sensor & Infrared Sensor",
          topics: "Touch sensor, Infrared sensor",
          desc: "Explore operating principles of the Touch sensor and reflective Infrared sensor for surface reading and mechanical contact."
        }, {
          stage: "Stage 2",
          sessions: "Sessions 3 – 4",
          title: "Collision Detection & Passive Pushing",
          topics: "Collision detection, Passive pushing",
          desc: "Design passive sweeping arms (Passive pushing) and construct immediate reactive behaviors upon impact (Collision detection)."
        }, {
          stage: "Stage 3",
          sessions: "Sessions 5 – 6",
          title: "Obstacle Avoidance & If-Else Branching",
          topics: "Obstacle avoidance, If-Else",
          desc: "Utilize infrared proximity reading for remote obstacle avoidance integrated with If-Else conditional steering."
        }, {
          stage: "Stage 4",
          sessions: "Sessions 7 – 8",
          title: "Line Stopping & Anti-fall Safety",
          topics: "Line stopping, Anti-fall",
          desc: "Calibrate surface reflectivity for line stopping at track markers and implement edge-protection algorithms (Anti-fall)."
        }, {
          stage: "Stage 5",
          sessions: "Sessions 9 – 10",
          title: "Counting Lines, Loops & Counters",
          topics: "Counting lines, Loops, Counters",
          desc: "Employ structured repeat loops and numerical counters to execute line-counting missions and milestone navigation."
        }, {
          stage: "Stage 6",
          sessions: "Sessions 11 – 12",
          title: "Random Logic, Timer & Capstone",
          topics: "Random, Timer",
          desc: "Harness hardware timers and randomized behaviors for dynamic autonomous maneuvering, concluding in capstone project defense."
        }]
      },
      methodology: {
        tag: "4-Step Pedagogical Model",
        title: "Structured Learning Framework",
        desc: "A consistent 4-step engineering method guiding students from real-world problem analysis to code-driven robotic defense.",
        steps: [{
          step: "01",
          title: "Warm-up",
          desc: "Observe real scenarios, analyze collision and sensing challenges, and formulate robotic reaction goals."
        }, {
          step: "02",
          title: "Assemble",
          desc: "Build vehicle chassis, calibrate passive pushing mechanisms, and secure touch and infrared sensors."
        }, {
          step: "03",
          title: "Program",
          desc: "Develop block algorithms with If-Else, loops, and counters to process sensor telemetry in real time."
        }, {
          step: "04",
          title: "Present",
          desc: "Deploy on physical arena mats, fine-tune thresholds, and confidently defend engineering solutions."
        }]
      },
      equipmentCard: {
        tag: "Hardware & Platform",
        title: "WhalesBot AI Module 1S Kit",
        desc: "International standard educational robotics system with smart multi-port controllers, precision motors, and reactive sensors.",
        refCostLabel: "Reference Equipment Cost",
        refCostValue: "~6,500,000 VNĐ / kit",
        refCostNote: "* This is reference equipment cost (not course tuition fee).",
        specs: [{
          label: "Hardware Kit",
          value: "WhalesBot AI Module 1S"
        }, {
          label: "Device Needed",
          value: "Computer required (Laptop/PC for coding)"
        }, {
          label: "Hardware Sensors",
          value: "Touch sensor & Infrared reflective sensor"
        }, {
          label: "Software IDE",
          value: "WhalesBot Block-based Scratch platform"
        }]
      },
      groupModel: {
        tag: "Classroom Architecture",
        title: "Small-Group Collaborative Model",
        desc: "Optimizing 1-on-1 mentorship, engineering role rotation, and project execution clarity.",
        idealTitle: "2 students / kit (Ideal ratio)",
        idealDesc: "Most recommended setup: 1 student leads mechanical build & hardware alignment, 1 student leads block logic & program uploads.",
        optTitle: "3 students / kit (Cost-optimized)",
        optDesc: "Structured rotation across Build, Coding, and Arena Testing ensures every student masters hands-on competencies."
      },
      finalProject: {
        tag: "Sample Capstone Project",
        name: "Schoolyard Cleaning Robot",
        nameEn: "Schoolyard Cleaning Robot",
        desc: "HP4's official exemplar capstone project: Autonomous vehicle combining passive pushing arms, impact touch sensing, and anti-fall infrared detection to safely sweep school arenas.",
        highlights: ["Autonomously sweeps floor debris using passive mechanical arms", "Instant reactive response upon obstacle contact: reverses and turns", "Infrared edge detection prevents dropping off table surfaces", "Line and event counting enables automated parking upon task completion"]
      },
      roadmapSection: {
        tag: "10-Node Pathway Map",
        title: "HP4's Position in Robotics Pathway",
        desc: "Explore all 10 modules from Early Discovery (D1–D4) to Elementary & Secondary Engineering (HP1–HP6).",
        currentBadge: "Current"
      },
      nextStepCard: {
        tag: "Next Progression",
        title: "Next Course: HP5 – Autonomous City",
        titleEn: "Autonomous City",
        desc: "Having mastered collision detection and reactive safety in HP4, students enter multi-agent smart mobility: traffic signal recognition and multi-hub urban logistics.",
        cta: "VIEW HP5 →"
      },
      focusHeader: "HP4 Core Sensor & Algorithmic Pillars",
      sensorsHeader: "Sensors & Physical Interaction",
      codingHeader: "Algorithmic Logic & Coding",
      prevBtn: "← HP3 — First Robot Car",
      nextBtn: "HP5 — Autonomous City →",
      trialCta: "Book a trial for this level"
    }
  }[lang];
  const roadmapNodes = [{
    code: "D1",
    path: "/RoboticsCourseD1Page",
    nameVi: "Mầm non 1",
    nameEn: "Early 1",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
  }, {
    code: "D2",
    path: "/RoboticsCourseD2Page",
    nameVi: "Mầm non 2",
    nameEn: "Early 2",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
  }, {
    code: "D3",
    path: "/RoboticsCourseD3Page",
    nameVi: "Mầm non 3",
    nameEn: "Early 3",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
  }, {
    code: "D4",
    path: "/RoboticsCourseD4Page",
    nameVi: "Mầm non 4",
    nameEn: "Early 4",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
  }, {
    code: "HP1",
    path: "/RoboticsCourseHP1Page",
    nameVi: "Khám phá cơ cấu",
    nameEn: "Mechanisms",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
  }, {
    code: "HP2",
    path: "/RoboticsCourseHP2Page",
    nameVi: "Cơ cấu nâng cao",
    nameEn: "Adv. Mechanics",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
  }, {
    code: "HP3",
    path: "/RoboticsCourseHP3Page",
    nameVi: "Xe robot đầu tiên",
    nameEn: "First Car",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
  }, {
    code: "HP4",
    path: "/RoboticsCourseHP4Page",
    nameVi: "Xe robot thông minh",
    nameEn: "Smart Vehicle",
    current: true,
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
  }, {
    code: "HP5",
    path: "/RoboticsCourseHp5Page",
    nameVi: "Thành phố xe tự lái",
    nameEn: "Autonomous City",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
  }, {
    code: "HP6",
    path: "/RoboticsCourseHP6Page",
    nameVi: "Hệ thống tự động",
    nameEn: "Automated Systems",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
  }];
  const sensorPillars = [{
    vi: "Cảm biến chạm (Touch sensor)",
    en: "Touch sensor"
  }, {
    vi: "Cảm biến hồng ngoại (Infrared sensor)",
    en: "Infrared sensor"
  }, {
    vi: "Phát hiện va chạm (Collision detection)",
    en: "Collision detection"
  }, {
    vi: "Tránh chướng ngại vật (Obstacle avoidance)",
    en: "Obstacle avoidance"
  }, {
    vi: "Dừng theo vạch (Line stopping)",
    en: "Line stopping"
  }, {
    vi: "Chống rơi vực (Anti-fall)",
    en: "Anti-fall safety edge"
  }, {
    vi: "Đếm vạch (Counting lines)",
    en: "Counting lines"
  }, {
    vi: "Đẩy vật cản thụ động (Passive pushing)",
    en: "Passive pushing mechanism"
  }];
  const logicPillars = [{
    vi: "Cấu trúc điều kiện If – Else",
    en: "If – Else condition branching"
  }, {
    vi: "Vòng lặp tuần hoàn (Loops)",
    en: "Repeat loops & logic cycles"
  }, {
    vi: "Biến đếm số lần (Counters)",
    en: "Step & event counters"
  }, {
    vi: "Giá trị ngẫu nhiên (Random)",
    en: "Random behavior generator"
  }, {
    vi: "Bộ đếm thời gian (Timer)",
    en: "Accurate timer triggers"
  }];
  return <section id="roboticscoursehp4detail" data-nav="light" className="bg-background text-foreground py-12 md:py-20 transition-colors">
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* (1) Full 4-level Breadcrumbs & Language Switcher Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-muted-foreground">
            <Link to="/HomePage" className="hover:text-primary transition-colors font-medium hover:underline">
              {t.breadcrumbs.home}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <Link to="/RoboticsLearningPathwayPage" className="hover:text-primary transition-colors font-medium hover:underline">
              {t.breadcrumbs.pathway}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <Link to="/RoboticsLearningPathwayPage" className="hover:text-primary transition-colors font-medium hover:underline">
              {t.breadcrumbs.ageTrack}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <span className="font-semibold text-foreground tracking-tight" aria-current="page">
              {t.breadcrumbs.current}
            </span>
          </nav>

          <div className="flex items-center self-end sm:self-auto gap-1.5 bg-card border border-border p-1 rounded-lg shadow-2xs">
            <Languages className="size-4 text-muted-foreground ml-1.5 mr-0.5" />
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => setLang("vi")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${lang === "vi" ? "bg-primary text-primary-foreground shadow-2xs" : "text-muted-foreground hover:text-foreground"}`}>
              Tiếng Việt
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => setLang("en")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${lang === "en" ? "bg-primary text-primary-foreground shadow-2xs" : "text-muted-foreground hover:text-foreground"}`}>
              English
            </Button>
          </div>
        </div>

        {/* Back Link to Pathway */}
        <div className="mb-6">
          <Link to="/RoboticsLearningPathwayPage" className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-primary hover:text-primary/80 transition-colors group">
            <ArrowLeft className="size-4 group-hover:-translate-x-1 transition-transform" />
            <span>{t.backPathway}</span>
          </Link>
        </div>

        {/* (2) Hero Course Header Card with Dual CTAs & Wheeled Robot Visual */}
        <div className="rounded-xl border border-border bg-card shadow-sm p-6 md:p-10 mb-10 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge className="bg-primary text-primary-foreground font-bold px-3 py-1 text-sm tracking-wider rounded-md">
                  {t.courseCode}
                </Badge>
                <Badge variant="outline" className="border-border text-foreground/80 font-medium text-xs rounded-md">
                  WhalesBot AI Module 1S
                </Badge>
                <Badge variant="secondary" className="font-medium text-xs rounded-md">
                  {t.metaAge}
                </Badge>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-3 font-serif">
                {t.title}
              </h1>
              <p className="text-muted-foreground text-base md:text-lg max-w-3xl mb-6">
                {t.subtitle}
              </p>

              {/* Core Concept Quote Banner */}
              <div className="bg-muted/70 border-l-4 border-primary rounded-r-lg p-4 md:p-5 flex items-start gap-3 mb-8">
                <Sparkles className="size-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    {lang === "vi" ? "Triết lý học phần" : "Core Educational Philosophy"}
                  </p>
                  <blockquote className="text-base md:text-lg font-semibold text-foreground mt-0.5 italic font-serif">
                    "{t.quote}"
                  </blockquote>
                </div>
              </div>

              {/* Dual CTAs in Hero */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-6 shadow-sm rounded-lg">
                  <Link to="/FreeTrialContactPage">
                    <span>{t.heroConsultCta}</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6 rounded-lg">
                  <Link to="/RoboticsLearningPathwayPage">
                    <span>{t.heroPathwayCta}</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Hero Visual: Wheeled robot approaching an obstacle */}
            <div className="lg:col-span-5">
              <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30 shadow-md group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wheeled_robot_obstacle_sensors.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wheeled_robot_obstacle_sensors.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wheeled_robot_obstacle_sensors.png-wvc-sizes" alt="Wheeled robot with touch and infrared sensors approaching an obstacle" loading="lazy" className="w-full h-auto max-h-[360px] object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 text-white">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="font-bold tracking-wide uppercase text-accent">IR & Touch Telemetry</span>
                    <span className="bg-primary/80 text-white px-2 py-0.5 rounded-md text-[10px] font-semibold">Active Sensing</span>
                  </div>
                  <p className="text-xs text-white/90 mt-1 font-sans">
                    {lang === "vi" ? "Mô hình xe 2 bánh tích hợp cảm biến hồng ngoại & công tắc hành trình" : "2-wheeled chassis equipped with IR proximity & bumper switches"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Specification Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-12">
          <Card className="border border-border/80 shadow-2xs">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                <span className="text-xs font-medium">{t.labels.age}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-base font-bold text-foreground">{t.metaAge}</p>
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-2xs">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Cpu className="size-4 text-primary" />
                <span className="text-xs font-medium">{t.labels.platform}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm font-bold text-foreground leading-snug">{t.metaPlatform}</p>
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-2xs">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="size-4 text-primary" />
                <span className="text-xs font-medium">{t.labels.duration}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm font-bold text-foreground leading-snug">{t.metaDuration}</p>
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-2xs">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="size-4 text-primary" />
                <span className="text-xs font-medium">{t.labels.groupSize}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm font-bold text-foreground leading-snug">{t.metaGroup}</p>
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-2xs col-span-2 sm:col-span-1">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="size-4 text-secondary" />
                <span className="text-xs font-medium">{t.labels.prerequisite}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm font-bold text-foreground leading-snug">{t.metaPrereq}</p>
            </CardContent>
          </Card>
        </div>

        {/* (3) 'Who is this course for?' Section with Obstacle Avoidance Visual */}
        <Card className="border border-border shadow-xs mb-14">
          <CardHeader className="p-6 md:p-8 border-b border-border/60">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-1">
              <HelpCircle className="size-4" />
              <span>{t.targetSection.tag}</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {t.targetSection.title}
            </CardTitle>
            <CardDescription className="text-base text-foreground/90 font-medium mt-2 max-w-3xl">
              {t.targetSection.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {t.targetSection.highlights.map((point, idx) => <div key={idx} data-index={idx} className="flex items-start gap-3 bg-muted/40 p-4 rounded-lg border border-border/60 hover:border-secondary/60 transition-colors">
                      <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground font-medium leading-relaxed">{point}</p>
                    </div>)}
                </div>
              </div>

              {/* Mid-page Visual: Robot detecting & steering around an obstacle */}
              <div className="lg:col-span-5">
                <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30 shadow-md group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/obstacle_avoidance_steering_robot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/obstacle_avoidance_steering_robot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/obstacle_avoidance_steering_robot.png-wvc-sizes" alt="Obstacle avoidance behavior illustrating robot detecting barrier and steering around" loading="lazy" className="w-full h-auto max-h-[320px] object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]" />
                  <div className="p-3 bg-card border-t border-border flex items-center justify-between text-xs">
                    <span className="font-semibold text-foreground">Thuật toán tránh vật cản If-Else</span>
                    <span className="text-primary font-bold font-mono">Obstacle Avoidance</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preserved Core Pillars (Sensors & Algorithmic Logic) */}
        <div className="space-y-6 mb-14">
          <div className="flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            <h2 className="text-2xl font-bold tracking-tight text-foreground font-serif">
              {t.focusHeader}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sensory & Physical Mechanics */}
            <Card className="border border-border shadow-xs">
              <CardHeader className="p-6 pb-4 border-b border-border/50">
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <span className="size-2 rounded-lg bg-primary" />
                  {t.sensorsHeader}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {sensorPillars.map((item, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2 bg-muted/40 hover:bg-muted/70 p-2.5 rounded-lg border border-border/40 transition-colors">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs md:text-sm font-medium text-foreground leading-snug">
                        {lang === "vi" ? item.vi : item.en}
                      </span>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Computational Thinking & Algorithm Logic */}
            <Card className="border border-border shadow-xs">
              <CardHeader className="p-6 pb-4 border-b border-border/50">
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <span className="size-2 rounded-lg bg-secondary" />
                  {t.codingHeader}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {logicPillars.map((item, idx) => <div key={idx} data-index={idx} className="flex items-center gap-3 bg-muted/40 hover:bg-muted/70 p-3 rounded-lg border border-border/40 transition-colors">
                      <Badge variant="outline" className="text-xs font-mono bg-background rounded-md">
                        0{idx + 1}
                      </Badge>
                      <span className="text-sm font-semibold text-foreground">
                        {lang === "vi" ? item.vi : item.en}
                      </span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* (4) 12-session Timeline Grouped into 6 Stages */}
        <Card className="border border-border shadow-xs mb-14">
          <CardHeader className="p-6 md:p-8 border-b border-border/60">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-1">
              <Milestone className="size-4" />
              <span>{t.timeline.tag}</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {t.timeline.title}
            </CardTitle>
            <CardDescription className="text-sm md:text-base text-muted-foreground mt-2 max-w-3xl">
              {t.timeline.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {t.timeline.stages.map((stg, idx) => <div key={idx} data-index={idx} className="p-5 rounded-lg border border-border/70 bg-card hover:border-primary/50 transition-colors flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="outline" className="text-xs font-semibold border-primary/40 text-primary rounded-md">
                        {stg.stage}
                      </Badge>
                      <span className="text-xs font-semibold text-muted-foreground font-mono">
                        {stg.sessions}
                      </span>
                    </div>
                    <h3 className="font-bold text-base text-foreground font-serif mb-1">
                      {stg.title}
                    </h3>
                    <div className="text-xs text-secondary font-mono font-semibold mb-2">
                      {stg.topics}
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {stg.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* (5) 4-Step Learning Method Section */}
        <div className="mb-14">
          <div className="mb-6">
            <div className="flex items-center gap-2 text-secondary font-bold text-xs uppercase tracking-wider mb-1">
              <Compass className="size-4" />
              <span>{t.methodology.tag}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {t.methodology.title}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              {t.methodology.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.methodology.steps.map((st, idx) => {
            const icons = [<PlayCircle key="0" className="size-6 text-primary" />, <Wrench key="1" className="size-6 text-secondary" />, <Code2 key="2" className="size-6 text-accent" />, <Presentation key="3" className="size-6 text-primary" />];
            return <Card key={idx} data-index={idx} className="border border-border shadow-xs hover:border-secondary transition-colors relative">
                  <CardHeader className="p-5 pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2.5 rounded-lg bg-muted">
                        {icons[idx]}
                      </div>
                      <span className="font-mono text-2xl font-bold text-muted-foreground/50">
                        {st.step}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-bold font-serif text-foreground">
                      {st.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 pt-1">
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {st.desc}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* (6) Equipment Card & (7) Small-Group Learning Model */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
          {/* (6) Equipment card: WhalesBot AI Module 1S, reference equipment cost ~6,500,000 VNĐ/kit, computer required */}
          <Card className="border border-border shadow-xs">
            <CardHeader className="p-6 md:p-8 border-b border-border/60">
              <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-1">
                <Cpu className="size-4" />
                <span>{t.equipmentCard.tag}</span>
              </div>
              <CardTitle className="text-2xl font-bold font-serif text-foreground">
                {t.equipmentCard.title}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground mt-2">
                {t.equipmentCard.desc}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-8 space-y-4">
              {/* Added WhalesBot AI Module 1S robotics kit image */}
              <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30 group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S robotics kit used in ROBOTO's HP1–HP6 engineering track" loading="lazy" className="w-full h-44 sm:h-52 object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]" />
              </div>

              <div className="p-4 rounded-lg bg-muted/40 border border-border/70 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <Coins className="size-4 text-accent shrink-0" />
                  <span>{t.equipmentCard.refCostLabel}:</span>
                </div>
                <span className="font-extrabold text-base md:text-lg text-primary font-mono">
                  {t.equipmentCard.refCostValue}
                </span>
              </div>
              <p className="text-xs text-muted-foreground italic">
                {t.equipmentCard.refCostNote}
              </p>

              <div className="space-y-2.5 pt-2">
                {t.equipmentCard.specs.map((sp, idx) => <div key={idx} data-index={idx} className="p-3 rounded-lg bg-card border border-border/50 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <div className="flex items-center gap-2">
                      {idx === 1 ? <Laptop className="size-4 text-primary shrink-0" /> : <CheckCircle2 className="size-4 text-secondary shrink-0" />}
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {sp.label}
                      </span>
                    </div>
                    <span className="text-xs md:text-sm font-bold text-foreground sm:text-right">
                      {sp.value}
                    </span>
                  </div>)}
              </div>
            </CardContent>
          </Card>

          {/* (7) Small-Group Learning Model Section */}
          <Card className="border border-border shadow-xs">
            <CardHeader className="p-6 md:p-8 border-b border-border/60">
              <div className="flex items-center gap-2 text-secondary font-bold text-xs uppercase tracking-wider mb-1">
                <Users className="size-4" />
                <span>{t.groupModel.tag}</span>
              </div>
              <CardTitle className="text-2xl font-bold font-serif text-foreground">
                {t.groupModel.title}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground mt-2">
                {t.groupModel.desc}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-8 space-y-4">
              <div className="p-4 rounded-lg bg-muted/40 border-l-4 border-secondary space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs font-bold rounded-md">
                    2 Học sinh
                  </Badge>
                  <h4 className="font-bold text-foreground text-sm md:text-base font-serif">
                    {t.groupModel.idealTitle}
                  </h4>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {t.groupModel.idealDesc}
                </p>
              </div>

              <div className="p-4 rounded-lg bg-muted/40 border-l-4 border-primary space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-bold border-primary text-primary rounded-md">
                    3 Học sinh
                  </Badge>
                  <h4 className="font-bold text-foreground text-sm md:text-base font-serif">
                    {t.groupModel.optTitle}
                  </h4>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {t.groupModel.optDesc}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (8) Final Project Section: 'Robot dọn sân trường' */}
        <Card className="border border-border shadow-xs mb-14">
          <CardHeader className="p-6 md:p-8 border-b border-border/60">
            <div className="flex items-center gap-2 text-accent font-bold text-xs uppercase tracking-wider mb-1">
              <Award className="size-4" />
              <span>{t.finalProject.tag}</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {t.finalProject.name}
            </CardTitle>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">
              {t.finalProject.nameEn}
            </p>
            <CardDescription className="text-sm md:text-base text-muted-foreground mt-3 max-w-3xl">
              {t.finalProject.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {t.finalProject.highlights.map((item, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2.5 p-3 rounded-lg bg-muted/40 border border-border/50 text-xs md:text-sm text-foreground/90 font-medium">
                  <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* (9) 10-node Pathway Map with HP4 Highlighted */}
        <Card className="border border-border shadow-xs mb-14">
          <CardHeader className="p-6 md:p-8 border-b border-border/60">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-1">
              <Milestone className="size-4" />
              <span>{t.roadmapSection.tag}</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {t.roadmapSection.title}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-2">
              {t.roadmapSection.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5">
              {roadmapNodes.map((node, idx) => <div key={node.code} data-index={idx} className="flex flex-col">
                  {node.current ? <div className="h-full p-2 rounded-lg border-2 border-primary bg-primary/10 flex flex-col items-center text-center justify-between gap-1 shadow-xs">
                      <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0 font-bold rounded-md">
                        {t.roadmapSection.currentBadge}
                      </Badge>
                      <img src={node.img} data-wvc-srcset={node.srcset} data-wvc-sizes={node.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-10 object-cover rounded-md my-0.5 border border-primary/40 shadow-2xs" />
                      <span className="font-extrabold text-sm text-primary font-mono">{node.code}</span>
                      <span className="text-[11px] font-semibold text-foreground leading-tight">
                        {lang === "vi" ? node.nameVi : node.nameEn}
                      </span>
                    </div> : <Link to={node.path} className="h-full p-2 rounded-lg border border-border bg-card hover:border-secondary hover:bg-muted/40 transition-colors flex flex-col items-center text-center justify-between gap-1 group">
                      <span className="text-[10px] font-mono font-medium text-muted-foreground group-hover:text-foreground">
                        {idx < 4 ? "4–6Y" : "7–16Y"}
                      </span>
                      <img src={node.img} data-wvc-srcset={node.srcset} data-wvc-sizes={node.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-10 object-cover rounded-md my-0.5 border border-border group-hover:border-secondary/60 transition-colors" />
                      <span className="font-bold text-sm text-foreground group-hover:text-secondary font-mono">
                        {node.code}
                      </span>
                      <span className="text-[11px] text-muted-foreground group-hover:text-foreground leading-tight">
                        {lang === "vi" ? node.nameVi : node.nameEn}
                      </span>
                    </Link>}
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* (11) 'What's next' Card */}
        <Card className="border border-border shadow-xs bg-muted/30 mb-12">
          <CardHeader className="p-6 md:p-8 pb-3">
            <div className="flex items-center gap-2 text-secondary font-bold text-xs uppercase tracking-wider mb-1">
              <Compass className="size-4" />
              <span>{t.nextStepCard.tag}</span>
            </div>
            <CardTitle className="text-2xl font-bold font-serif text-foreground">
              {t.nextStepCard.title}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-2 max-w-2xl">
              {t.nextStepCard.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8 pt-2 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h4 className="text-lg md:text-xl font-bold text-primary font-serif">
                {t.nextStepCard.titleEn}
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground max-w-xl">
                {t.nextStepCard.desc}
              </p>
            </div>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold shrink-0 shadow-xs rounded-lg">
              <Link to="/RoboticsCourseHp5Page">
                <span>{t.nextStepCard.cta}</span>
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Separator className="mb-10" />

        {/* (10) Previous/Next Course Navigation at the bottom */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <Button asChild variant="outline" size="lg" className="border-border justify-start md:justify-center font-medium rounded-lg">
            <Link to="/RoboticsCourseHP3Page">
              <span className="truncate">{t.prevBtn}</span>
            </Link>
          </Button>

          <Button asChild variant="default" size="lg" className="font-semibold shadow-xs bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg">
            <Link to="/FreeTrialContactPage">
              <span>{t.trialCta}</span>
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="border-border justify-between md:justify-center font-medium rounded-lg">
            <Link to="/RoboticsCourseHp5Page">
              <span className="truncate">{t.nextBtn}</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}